import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  PageBlock,
  IconInfo,
  IconMail,
  IconTick,
  Inline,
  Text,
  TextLink,
  useResponsiveValue,
} from 'braid-design-system';
import type { ReactNode } from 'react';
import { savedSearchesPath } from 'src/config';
import SaveSearchButton from '../SaveSearchButton/SaveSearchButton';
import translations from './.vocab';
import * as styles from './SaveSearchPrompt.css';
import { SavedSearchStatus } from 'src/store/saveSearch/saveSearch';

const space = 'medium';

const SaveSearchSavedView = ({ isSearchSaved }: { isSearchSaved: boolean }) => {
  const { t } = useTranslations(translations);
  const isMobile =
    useResponsiveValue()({
      mobile: true,
      tablet: false,
    }) || false;

  return (
    <Columns space={space} reverse={isMobile} alignY="center">
      <Column width="content">
        {isSearchSaved ? <IconInfo size="large" /> : <IconTick size="large" />}
      </Column>
      <Column>
        <Text>
          {isSearchSaved
            ? t(
                'You’ve saved this search before. <link>Manage your Saved Searches</link>',
                {
                  link: (children: ReactNode) => (
                    <TextLink href={savedSearchesPath}>{children}</TextLink>
                  ),
                },
              )
            : t(
                'We’ve saved your search. <link>Manage your Saved Searches</link>',
                {
                  link: (children: ReactNode) => (
                    <TextLink href={savedSearchesPath}>{children}</TextLink>
                  ),
                },
              )}
        </Text>
      </Column>
    </Columns>
  );
};

const SaveSearchSuggestionView = () => {
  const { t } = useTranslations(translations);

  return (
    <Inline space={{ mobile: 'none', tablet: 'small' }} alignY="center">
      <Box display={{ mobile: 'none', tablet: 'block' }}>
        <IconMail size="large" />
      </Box>
      <Columns space={space} alignY="center">
        <Column>
          <Text>{t('Save this search and get new jobs by email')}</Text>
        </Column>
        <Column width="content">
          <SaveSearchButton
            size="small"
            dataAutomation="saveSearchButtonInPrompt"
          >
            {t('Save')}
          </SaveSearchButton>
        </Column>
      </Columns>
    </Inline>
  );
};

const renderSaveSearchPrompt = ({
  isSearchSaved,
  status,
}: {
  isSearchSaved: boolean;
  status: SavedSearchStatus;
}) => {
  if (isSearchSaved || status === SavedSearchStatus.SAVED) {
    return <SaveSearchSavedView isSearchSaved={isSearchSaved} />;
  }

  return <SaveSearchSuggestionView />;
};

export interface SaveSearchPromptProps {
  status: SavedSearchStatus;
  isSearchSaved: boolean;
  shadowPosition: 'top' | 'bottom';
}

const SaveSearchPrompt = ({
  status,
  isSearchSaved,
  shadowPosition,
}: SaveSearchPromptProps) => (
  <Box background="surface" position="relative" paddingY="xsmall">
    <Box
      position="absolute"
      boxShadow="small"
      top={0}
      left={0}
      right={0}
      bottom={0}
      className={[
        styles.shadowBox,
        { [styles.shadowRotate]: shadowPosition === 'top' },
      ]}
    />
    <PageBlock width="medium">
      <Box display="flex" justifyContent="center">
        {renderSaveSearchPrompt({ isSearchSaved, status })}
      </Box>
    </PageBlock>
  </Box>
);

export default SaveSearchPrompt;
